/**
 * AdminMintingDappsContainer
 *
 * Container component for managing minting dapps in the admin panel.
 * Integrates with MySQL backend via API.
 *
 * @module containers/AdminMintingDapps
 * @category Admin
 */

import React, { useState, useEffect, useCallback } from 'react';
import { STORAGE_KEY, REGISTRY_SYNC_KEY } from './constants';
import { mintingDappRegistry } from '../../minting/registry';
import { loadComponentFromCode } from '../../minting/componentLoader';
import type { MintingDapp } from '../../minting/types';
import { mintingDappAPI } from '../../minting/services/mintingDappAPI';
import DappList from './components/DappList';
import DappFormModal from './components/DappFormModal';
import ImportExportModal from './components/ImportExportModal';
import ConfirmModal from './components/ConfirmModal';
import './AdminMintingDapps.css';
import { sessionStorageGet, sessionStorageSet, sessionStorageRemove } from '../../lib/window-shim';

const AdminMintingDappsContainer = () => {
  // State
  const [dapps, setDapps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDapp, setEditingDapp] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [showImportExport, setShowImportExport] = useState(false);
  const [toast, setToast] = useState(null);
  const [exportDataString, setExportDataString] = useState('');

  // Helper: get auth token from sessionStorage
  const getAuthToken = useCallback(() => {
    const authKey = sessionStorageGet('authKey');
    const tokenStr = sessionStorageGet('token');
    if (!authKey || !tokenStr) return null;
    try {
      const parsed = JSON.parse(tokenStr);
      const userId = parsed?.account?.id;
      if (!userId) return null;
      return `Bearer ${String(authKey)}:${String(userId)}`;
    } catch (e) {
      console.warn('Failed to get auth token', e);
      return null;
    }
  }, []);

  // Load dapps from API on mount
  useEffect(() => {
    loadDapps();

    // Expose helper globally for ImportExportModal
    window.clearMintingDappsStorage = () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem('viribright_minting_dapps_synced');
      } catch (error) {
        console.error('Failed to clear storage:', error);
      }
    };

    return () => {
      delete window.clearMintingDappsStorage;
    };
  }, []);

  // Load dapps from API with fallback to localStorage backup
  const loadDapps = useCallback(async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        showToast('error', 'Not authenticated');
        return;
      }

      const result = await mintingDappAPI.getAll({ limit: 100 });
      if (result.success) {
        const apiDapps = result.data;
        const apiIds = new Set(apiDapps.map((d: any) => d.id));

        // Remove dapps from registry that are no longer in the API
        const currentIds = mintingDappRegistry.getIds();
        currentIds.forEach(regId => {
          if (!apiIds.has(regId)) {
            mintingDappRegistry.unregister(regId);
          }
        });

        // Merge with existing registry entries to preserve internal components
        apiDapps.forEach(apiDapp => {
          const existing = mintingDappRegistry.get(apiDapp.id);
          let finalDapp: MintingDapp = apiDapp as any;

          if (apiDapp.type === 'internal') {
            if (existing?.component) {
              // Preserve the component from the existing registry (pre-registered builtin)
              finalDapp = { ...apiDapp, component: existing.component };
            } else if (apiDapp.component_code) {
              // Load component from code string
              try {
                const component = loadComponentFromCode(apiDapp.component_code, apiDapp.id);
                finalDapp = { ...apiDapp, component };
              } catch (err) {
                console.error(`Failed to load component for ${apiDapp.id}:`, err);
                finalDapp = apiDapp;
              }
            }
          }
          // Register (overwrite if exists)
          mintingDappRegistry.register(finalDapp);
        });

        setDapps(mintingDappRegistry.getAll());

        // Backup raw API data to localStorage (excluding React components)
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(result.data, null, 2));
          localStorage.setItem(REGISTRY_SYNC_KEY, 'true');
        } catch (error) {
          console.error('Failed to backup to localStorage:', error);
        }
      } else {
        throw new Error('API request failed');
      }
    } catch (error) {
      console.error('Failed to load dapps from API:', error);
      // Fallback: load from localStorage backup
      const backup = localStorage.getItem(STORAGE_KEY);
      if (backup) {
        try {
          const backupDapps = JSON.parse(backup);
          backupDapps.forEach((dapp: any) => {
            mintingDappRegistry.register(dapp);
          });
          setDapps(mintingDappRegistry.getAll());
          showToast('info', 'Using backup storage (API unavailable)');
        } catch (e) {
          showToast('error', 'Backup data corrupted');
        }
      } else {
        showToast('error', 'Failed to load dapps. No backup available.');
      }
    }
  }, [getAuthToken]);

  // Show toast notification
  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Handle add new dapp
  const handleAdd = async (dappData: Partial<MintingDapp>) => {
    try {
      // Validate required fields (also validated by Joi)
      if (!dappData.id || !dappData.name || !dappData.type) {
        showToast('error', 'ID, Name, and Type are required');
        return;
      }

      // Validate ID format (kebab-case)
      const idRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
      if (!idRegex.test(dappData.id)) {
        showToast('error', 'ID must be lowercase kebab-case (e.g., "my-dapp")');
        return;
      }

      // Check if ID already exists
      if (mintingDappRegistry.has(dappData.id)) {
        showToast('error', `Dapp with ID "${dappData.id}" already exists. Use a different ID.`);
        return;
      }

      const authToken = getAuthToken();
      if (!authToken) {
        showToast('error', 'Not authenticated');
        return;
      }

      // Prepare payload for API (map component to component_code)
      const payload: any = {
        id: dappData.id,
        name: dappData.name,
        description: dappData.description || '',
        type: dappData.type,
        config: dappData.config,
        version: dappData.version || '1.0.0',
        author: dappData.author,
        icon: dappData.icon,
        tags: dappData.tags || [],
      };

      if (dappData.type === 'internal') {
        payload.component_code = dappData.component_code || '';
        if (!payload.component_code.trim()) {
          showToast('error', 'Component code is required for internal dapps');
          return;
        }
      } else if (dappData.type === 'external') {
        payload.url = dappData.url || '';
        if (!payload.url.trim()) {
          showToast('error', 'URL is required for external dapps');
          return;
        }
      }

      const result = await mintingDappAPI.create(payload, authToken);
      if (result.success) {
        showToast('success', `Dapp "${dappData.name}" added successfully!`);
        setShowForm(false);
        // Reload dapps from API to sync registry
        await loadDapps();
      } else {
        showToast('error', result.message || 'Failed to add dapp');
      }
    } catch (error: any) {
      console.error('Failed to add dapp:', error);
      showToast('error', `Failed to add dapp: ${error.message}`);
    }
  };

  // Handle update dapp
  const handleUpdate = async (dappData: Partial<MintingDapp>) => {
    try {
      if (!editingDapp) return;

      const id = editingDapp.id;

      // Don't allow changing ID
      if (dappData.id && dappData.id !== id) {
        showToast('error', 'Cannot change dapp ID');
        return;
      }

      const authToken = getAuthToken();
      if (!authToken) {
        showToast('error', 'Not authenticated');
        return;
      }

      // Prepare update payload
      const payload: any = {};
      if (dappData.name !== undefined) payload.name = dappData.name;
      if (dappData.description !== undefined) payload.description = dappData.description;
      if (dappData.type !== undefined) payload.type = dappData.type;
      if (dappData.component_code !== undefined) payload.component_code = dappData.component_code;
      if (dappData.url !== undefined) payload.url = dappData.url;
      if (dappData.config !== undefined) payload.config = dappData.config;
      if (dappData.version !== undefined) payload.version = dappData.version;
      if (dappData.author !== undefined) payload.author = dappData.author;
      if (dappData.icon !== undefined) payload.icon = dappData.icon;
      if (dappData.tags !== undefined) payload.tags = dappData.tags;
      if (dappData.has_on_init !== undefined) payload.has_on_init = dappData.has_on_init;
      if (dappData.has_on_destroy !== undefined) payload.has_on_destroy = dappData.has_on_destroy;

      const result = await mintingDappAPI.update(id, payload, authToken);
      if (result.success) {
        showToast('success', `Dapp "${dappData.name}" updated successfully!`);
        setEditingDapp(null);
        setShowForm(false);
        await loadDapps();
      } else {
        showToast('error', result.message || 'Failed to update dapp');
      }
    } catch (error: any) {
      console.error('Failed to update dapp:', error);
      showToast('error', `Failed to update dapp: ${error.message}`);
    }
  };

  // Handle delete dapp
  const handleDelete = async (dapp: MintingDapp) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        showToast('error', 'Not authenticated');
        return;
      }

      const result = await mintingDappAPI.delete(dapp.id, authToken);
      if (result.success) {
        showToast('success', `Dapp "${dapp.name}" deleted successfully!`);
        setShowConfirmDelete(null);
        await loadDapps();
      } else {
        showToast('error', result.message || 'Failed to delete dapp');
      }
    } catch (error: any) {
      console.error('Failed to delete dapp:', error);
      showToast('error', `Failed to delete dapp: ${error.message}`);
    }
  };

  // Handle import dapps (from JSON)
  const handleImport = async (jsonString: string) => {
    try {
      const imported = JSON.parse(jsonString);
      if (!Array.isArray(imported)) {
        showToast('error', 'Imported data must be an array of dapps');
        return;
      }

      const authToken = getAuthToken();
      if (!authToken) {
        showToast('error', 'Not authenticated');
        return;
      }

      const result = await mintingDappAPI.import(imported, authToken);
      if (result.success) {
        showToast('success', result.message);
        setShowImportExport(false);
        await loadDapps();
      } else {
        showToast('error', result.message || 'Import failed');
      }
    } catch (error: any) {
      showToast('error', `Import failed: ${error.message}`);
    }
  };

  // Prepare export data before opening modal
  const openImportExportModal = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        showToast('error', 'Not authenticated');
        return;
      }
      const result = await mintingDappAPI.export(authToken);
      if (result.success) {
        setExportDataString(JSON.stringify(result.data, null, 2));
      } else {
        setExportDataString('// Failed to fetch export data');
      }
    } catch (error) {
      console.error('Failed to fetch export data:', error);
      setExportDataString('// Error: could not load export data');
    }
    setShowImportExport(true);
  };

  // Handle export all dapps (returns JSON) - called by ImportExportModal
  const handleExport = () => {
    return exportDataString || JSON.stringify(mintingDappRegistry.getAll(), null, 2);
  };

  // Stats
  const stats = {
    total: dapps.length,
    internal: dapps.filter(d => d.type === 'internal').length,
    external: dapps.filter(d => d.type === 'external').length,
  };

  return (
    <div className="admin-minting-dapps">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="h4 mb-1">
                    <i className="fas fa-cogs me-2 text-primary"></i>
                    Minting Dapps Management
                  </h2>
                  <p className="text-muted mb-0 small">
                    Manage internal and external minting applications
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={openImportExportModal}
                    title="Import/Export dapps"
                  >
                    <i className="fas fa-exchange-alt me-2"></i>
                    Import/Export
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingDapp(null);
                      setShowForm(true);
                    }}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Add New Dapp
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`alert alert-${toast.type === 'error' ? 'danger' : toast.type} alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3`}
          style={{ zIndex: 9999, minWidth: '300px' }}
          role="alert">
          <i className={`fas fa-${toast.type === 'success' ? 'check-circle' : toast.type === 'error' ? 'exclamation-circle' : 'info-circle'} me-2`}></i>
          {toast.message}
          <button type="button" className="btn-close" onClick={() => setToast(null)}></button>
        </div>
      )}

      {/* Stats Row */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <h3 className="text-primary mb-0">{stats.total}</h3>
              <small className="text-muted">Total Dapps</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <h3 className="text-success mb-0">{stats.internal}</h3>
              <small className="text-muted">Internal</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <h3 className="text-info mb-0">{stats.external}</h3>
              <small className="text-muted">External</small>
            </div>
          </div>
        </div>
      </div>

      {/* Dapp List */}
      <div className="row">
        <div className="col-12">
          <DappList
            dapps={dapps}
            onEdit={setEditingDapp}
            onDelete={setShowConfirmDelete}
            onRefresh={loadDapps}
          />
        </div>
      </div>

      {/* Modals */}
      {showForm && (
        <DappFormModal
          dapp={editingDapp}
          onSave={editingDapp ? handleUpdate : handleAdd}
          onClose={() => {
            setShowForm(false);
            setEditingDapp(null);
          }}
        />
      )}

      {showConfirmDelete && (
        <ConfirmModal
          title="Delete Dapp"
          message={`Are you sure you want to delete "${showConfirmDelete.name}"? This action cannot be undone.`}
          onConfirm={() => handleDelete(showConfirmDelete)}
          onCancel={() => setShowConfirmDelete(null)}
          confirmText="Delete"
          confirmVariant="danger"
        />
      )}

      {showImportExport && (
        <ImportExportModal
          onExport={handleExport}
          onImport={handleImport}
          onClose={() => setShowImportExport(false)}
          dappCount={dapps.length}
        />
      )}
    </div>
  );
};

export default AdminMintingDappsContainer;
